import React, { useState, useRef } from 'react';
import { ChevronLeft, MapPin, Shield, MessageCircle, Share2, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Rating } from '../components/Rating';
import { DepositPolicyModal } from '../components/DepositPolicyModal';
import { getListingById } from '../utils/mockListings';
interface ItemDetailProps {
  itemId: string;
  onBack: () => void;
  onBooking: () => void;
  onChat: () => void;
  onOwnerClick: () => void;
}
export function ItemDetail({
  itemId,
  onBack,
  onBooking,
  onChat,
  onOwnerClick
}: ItemDetailProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [careExpanded, setCareExpanded] = useState(false);
  const reviewsRef = useRef<HTMLDivElement>(null);
  // Get listing data
  const listing = getListingById(itemId);
  // Fallback if listing not found
  if (!listing) {
    return <div className="min-h-screen bg-cream flex items-center justify-center" data-id="element-1236">
        <div className="text-center" data-id="element-1237">
          <p className="text-charcoal mb-4" data-id="element-1238">Item not found</p>
          <Button onClick={onBack} data-id="element-1239">Go Back</Button>
        </div>
      </div>;
  }
  const shortDescription = listing.description.length > 120 ? listing.description.slice(0, 120) + '...' : listing.description;
  const shortCare = listing.careInstructions.length > 100 ? listing.careInstructions.slice(0, 100) + '...' : listing.careInstructions;
  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="min-h-screen bg-cream pb-40" data-id="element-1240">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-sand/50" data-id="element-1241">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between" data-id="element-1242">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-sand/20 rounded-full transition-colors" data-id="element-1243">
            <ChevronLeft size={24} className="text-charcoal" data-id="element-1244" />
          </button>
          <button className="p-2 -mr-2 hover:bg-sand/20 rounded-full transition-colors" data-id="element-1245">
            <Share2 size={20} className="text-charcoal" data-id="element-1246" />
          </button>
        </div>
      </div>

      {/* Image Carousel */}
      <div className="relative bg-charcoal" data-id="element-1247">
        <div className="overflow-hidden" data-id="element-1248">
          <motion.div className="flex" animate={{
          x: `-${currentImageIndex * 100}%`
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} data-id="element-1249">
            {listing.images.map((image, index) => <img key={index} src={image} alt={`${listing.title} ${index + 1}`} className="w-full h-80 object-cover flex-shrink-0" data-id="element-1250" />)}
          </motion.div>
        </div>

        {/* Image Indicators */}
        {listing.images.length > 1 && <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-1251">
            {listing.images.map((_, index) => <button key={index} onClick={() => setCurrentImageIndex(index)} className={`h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 w-2'}`} data-id="element-1252" />)}
          </div>}
      </div>

      <div className="max-w-md mx-auto px-5 py-6 space-y-6" data-id="element-1253">
        {/* Title & Rating */}
        <div className="space-y-3" data-id="element-1254">
          <h1 className="text-2xl font-bold text-charcoal leading-tight" data-id="element-1255">
            {listing.title}
          </h1>

          <div className="flex items-center justify-between" data-id="element-1256">
            <button onClick={scrollToReviews} className="flex items-center gap-2 hover:opacity-70 transition-opacity" data-id="element-1257">
              <Rating value={listing.rating} size={16} data-id="element-1258" />
              <span className="text-sm font-medium text-charcoal" data-id="element-1259">
                {listing.rating}
              </span>
              <span className="text-sm text-moss-gray" data-id="element-1260">
                ({listing.reviewCount})
              </span>
            </button>
            <div className="flex items-center gap-1 text-sm text-moss-gray" data-id="element-1261">
              <MapPin size={14} data-id="element-1262" />
              <span data-id="element-1263">{listing.location}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap" data-id="element-1264">
            <Badge variant="success" size="sm" data-id="element-1265">
              {listing.condition}
            </Badge>
          </div>
        </div>

        {/* Owner & Pricing Side by Side */}
        <div className="grid grid-cols-2 gap-4" data-id="element-1266">
          {/* Owner Card */}
          <button onClick={onOwnerClick} className="bg-white rounded-ios-lg p-5 shadow-sm border border-sand/50 hover:border-dark-green/30 transition-all active:scale-[0.99] text-left" data-id="element-1267">
            <div className="text-sm font-semibold text-charcoal mb-3" data-id="element-1268">
              Owner
            </div>
            <div className="flex items-center gap-3" data-id="element-1269">
              <img src={listing.owner.avatar} alt={listing.owner.name} className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0" data-id="element-1270" />
              <div data-id="element-1271">
                <div className="flex items-center gap-1" data-id="element-1272">
                  <span className="font-semibold text-charcoal text-sm" data-id="element-1273">
                    {listing.owner.name}
                  </span>
                  {listing.owner.verified && <Shield size={14} className="text-dark-green flex-shrink-0" data-id="element-1274" />}
                </div>
                <div className="text-xs text-moss-gray" data-id="element-1275">
                  12 bookings as lender
                </div>
              </div>
            </div>
          </button>

          {/* Pricing Card */}
          <div className="bg-white rounded-ios-lg p-5 shadow-sm border border-sand/50" data-id="element-1276">
            <div className="text-sm font-semibold text-charcoal mb-3" data-id="element-1277">
              Pricing
            </div>
            <div className="space-y-3" data-id="element-1278">
              <div data-id="element-1279">
                <div className="text-3xl font-bold text-dark-green leading-none mb-1" data-id="element-1280">
                  ${listing.pricing.day}
                </div>
                <div className="text-xs text-moss-gray" data-id="element-1281">per day</div>
              </div>
              <div className="pt-3 border-t border-sand/50" data-id="element-1282">
                <div className="flex items-center justify-between mb-1" data-id="element-1283">
                  <span className="text-xs text-moss-gray" data-id="element-1284">Deposit (hold)</span>
                  <button onClick={() => setShowDepositModal(true)} className="p-1 hover:bg-sand/20 rounded transition-colors" data-id="element-1285">
                    <Info size={14} className="text-dark-green" data-id="element-1286" />
                  </button>
                </div>
                <div className="text-xl font-bold text-charcoal" data-id="element-1287">
                  ${listing.deposit}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-ios-lg p-5 shadow-sm border border-sand/50" data-id="element-1288">
          <h3 className="font-semibold text-charcoal text-lg mb-3" data-id="element-1289">
            Description
          </h3>
          <AnimatePresence mode="wait" data-id="element-1290">
            <motion.div key={descriptionExpanded ? 'expanded' : 'collapsed'} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-1291">
              <p className="text-charcoal/80 leading-relaxed text-sm" data-id="element-1292">
                {descriptionExpanded ? listing.description : shortDescription}
              </p>
            </motion.div>
          </AnimatePresence>
          {listing.description.length > 120 && <button onClick={() => setDescriptionExpanded(!descriptionExpanded)} className="mt-3 text-sm font-medium text-dark-green hover:underline flex items-center gap-1" data-id="element-1293">
              {descriptionExpanded ? <>
                  See less <ChevronUp size={16} data-id="element-1294" />
                </> : <>
                  See more <ChevronDown size={16} data-id="element-1295" />
                </>}
            </button>}
        </div>

        {/* Care Instructions */}
        <div className="bg-white rounded-ios-lg p-5 shadow-sm border border-sand/50" data-id="element-1296">
          <h3 className="font-semibold text-charcoal text-lg mb-3" data-id="element-1297">
            Care Instructions
          </h3>
          <AnimatePresence mode="wait" data-id="element-1298">
            <motion.div key={careExpanded ? 'expanded' : 'collapsed'} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-1299">
              <p className="text-charcoal/80 leading-relaxed text-sm" data-id="element-1300">
                {careExpanded ? listing.careInstructions : shortCare}
              </p>
            </motion.div>
          </AnimatePresence>
          {listing.careInstructions.length > 100 && <button onClick={() => setCareExpanded(!careExpanded)} className="mt-3 text-sm font-medium text-dark-green hover:underline flex items-center gap-1" data-id="element-1301">
              {careExpanded ? <>
                  See less <ChevronUp size={16} data-id="element-1302" />
                </> : <>
                  See more <ChevronDown size={16} data-id="element-1303" />
                </>}
            </button>}
        </div>

        {/* Reviews Section */}
        <div ref={reviewsRef} className="bg-white rounded-ios-lg p-5 shadow-sm border border-sand/50 scroll-mt-20" data-id="element-1304">
          <div className="flex items-center justify-between mb-4" data-id="element-1305">
            <h3 className="font-semibold text-charcoal text-lg" data-id="element-1306">Reviews</h3>
            <div className="flex items-center gap-2" data-id="element-1307">
              <Rating value={listing.rating} size={16} data-id="element-1308" />
              <span className="text-sm font-medium text-charcoal" data-id="element-1309">
                {listing.rating}
              </span>
            </div>
          </div>

          <div className="space-y-4" data-id="element-1310">
            {listing.reviews.map((review, index) => <div key={review.id} className={`pb-4 ${index !== listing.reviews.length - 1 ? 'border-b border-sand/50' : ''}`} data-id="element-1311">
                <div className="flex items-start justify-between mb-2" data-id="element-1312">
                  <span className="font-medium text-charcoal text-sm" data-id="element-1313">
                    {review.author}
                  </span>
                  <Rating value={review.rating} size={14} data-id="element-1314" />
                </div>
                <p className="text-sm text-charcoal/80 leading-relaxed mb-2" data-id="element-1315">
                  {review.text}
                </p>
                <span className="text-xs text-moss-gray" data-id="element-1316">{review.date}</span>
              </div>)}
          </div>

          {listing.reviewCount > listing.reviews.length && <button className="w-full text-center text-sm font-medium text-dark-green hover:underline py-2 mt-2" data-id="element-1317">
              View all {listing.reviewCount} reviews
            </button>}
        </div>
      </div>

      {/* Fixed Bottom CTA - Above TabBar */}
      <div className="fixed bottom-20 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-sand/50 p-4 z-40 shadow-lg" data-id="element-1318">
        <div className="max-w-md mx-auto flex gap-3" data-id="element-1319">
          <Button variant="secondary" onClick={onChat} className="w-14 h-14 p-0 flex items-center justify-center rounded-full flex-shrink-0" data-id="element-1320">
            <MessageCircle size={22} data-id="element-1321" />
          </Button>
          <Button variant="primary" fullWidth onClick={onBooking} className="h-14 text-base font-semibold" data-id="element-1322">
            Request Booking
          </Button>
        </div>
      </div>

      {/* Deposit Policy Modal */}
      <DepositPolicyModal isOpen={showDepositModal} onClose={() => setShowDepositModal(false)} depositAmount={listing.deposit} data-id="element-1323" />
    </div>;
}